import { z } from 'zod';

export const XPANSE_AUTH_TYPES = ['STANDARD', 'ADVANCED'] as const;
export const XpanseAuthTypeSchema = z.enum(XPANSE_AUTH_TYPES);
export type XpanseAuthType = z.infer<typeof XpanseAuthTypeSchema>;

// Sourced from the official XSOAR Xpanse integration; nullable on the alert schema
// so unrecognized statuses do not cause us to drop the alert.
export const XPANSE_ALERT_STATUSES = [
  'new',
  'reopened',
  'under_investigation',
  'resolved_no_longer_observed',
  'resolved_no_risk',
  'resolved_risk_accepted',
  'resolved_contested_asset',
  'resolved_remediated_automatically',
  'resolved',
] as const;
export const XpanseAlertStatusSchema = z.enum(XPANSE_ALERT_STATUSES);
export type XpanseAlertStatus = z.infer<typeof XpanseAlertStatusSchema>;

export const XPANSE_ALERT_SEVERITIES = [
  'critical',
  'high',
  'medium',
  'low',
  'informational',
  'unknown',
] as const;
export const XpanseAlertSeveritySchema = z.enum(XPANSE_ALERT_SEVERITIES);
export type XpanseAlertSeverity = z.infer<typeof XpanseAlertSeveritySchema>;

// Xpanse rejects unknown as a `severity` filter value even though alerts can be
// returned with it.
export const XPANSE_ALERT_SEVERITY_FILTERS = [
  'critical',
  'high',
  'medium',
  'low',
  'informational',
] as const satisfies ReadonlyArray<XpanseAlertSeverity>;
export const XpanseAlertSeverityFilterSchema = z.enum(
  XPANSE_ALERT_SEVERITY_FILTERS,
);
export type XpanseAlertSeverityFilter = z.infer<
  typeof XpanseAlertSeverityFilterSchema
>;

const lowercased = <TSchema extends z.ZodTypeAny>(schema: TSchema) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? value.toLowerCase() : value),
    schema,
  );

export const XpanseAlertEventSchema = z.object({
  action_remote_ip: z.string().nullable().optional(),
  action_remote_port: z.number().nullable().optional(),
  action_external_hostname: z.string().nullable().optional(),
  agent_fqdn: z.string().nullable().optional(),
});
export type XpanseAlertEvent = z.infer<typeof XpanseAlertEventSchema>;

export const XpanseAlertCertificateSchema = z.object({
  issuerName: z.string().nullable().optional(),
  subjectName: z.string().nullable().optional(),
  validNotBefore: z.number().nullable().optional(),
  validNotAfter: z.number().nullable().optional(),
  serialNumber: z.string().nullable().optional(),
});
export type XpanseAlertCertificate = z.infer<
  typeof XpanseAlertCertificateSchema
>;

export const XpanseAlertAssetIdentifierSchema = z.object({
  domain: z.string().nullable().optional(),
  ipv4Address: z.string().nullable().optional(),
  ipv6Address: z.string().nullable().optional(),
  httpPath: z.string().nullable().optional(),
  portNumber: z.number().nullable().optional(),
  portProtocol: z.string().nullable().optional(),
  firstObserved: z.number().nullable().optional(),
  lastObserved: z.number().nullable().optional(),
  certificate: XpanseAlertCertificateSchema.nullable().optional(),
});
export type XpanseAlertAssetIdentifier = z.infer<
  typeof XpanseAlertAssetIdentifierSchema
>;

const optionalStringList = z.array(z.string()).nullable().optional();

export const XpanseAlertSchema = z.object({
  alert_id: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  severity: lowercased(XpanseAlertSeveritySchema)
    .nullable()
    .optional()
    .catch(null),
  status: lowercased(XpanseAlertStatusSchema).nullable().optional().catch(null),
  resolution_status: z.string().nullable().optional(),
  creation_time: z.number().nullable().optional(),
  server_creation_time: z.number().nullable().optional(),
  local_insert_ts: z.number().nullable().optional(),
  detection_timestamp: z.number().nullable().optional(),
  last_observed: z.number().nullable().optional(),
  last_modified_ts: z.number().nullable().optional(),
  host_name: z.string().nullable().optional(),
  host_ip: z.string().nullable().optional(),
  alert_type: z.string().nullable().optional(),
  alert_source: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  external_id: z.string().nullable().optional(),
  asm_alert_categories: optionalStringList,
  attack_surface_rule_id: z.string().nullable().optional(),
  attack_surface_rule_name: z.string().nullable().optional(),
  remediation_guidance: z.string().nullable().optional(),
  domain_names: optionalStringList,
  ipv4_addresses: optionalStringList,
  ipv6_addresses: optionalStringList,
  asset_ids: optionalStringList,
  service_ids: optionalStringList,
  website_ids: optionalStringList,
  asset_identifiers: z
    .array(XpanseAlertAssetIdentifierSchema)
    .nullable()
    .optional(),
  port_number: z.number().nullable().optional(),
  port_protocol: z.string().nullable().optional(),
  certificate: XpanseAlertCertificateSchema.nullable().optional(),
  action_external_hostname: z.string().nullable().optional(),
  action_remote_ip: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
  action_remote_port: z
    .union([z.number(), z.array(z.number())])
    .nullable()
    .optional(),
  dynamic_fields: z.record(z.string(), z.unknown()).nullable().optional(),
  events: z.array(XpanseAlertEventSchema).catch([]),
});
export type XpanseAlert = z.infer<typeof XpanseAlertSchema>;

const RESOLUTION_STATUS_TOKEN_TO_ALERT_STATUS: ReadonlyArray<
  readonly [string, XpanseAlertStatus]
> = [
  ['RESOLVED_NO_LONGER_OBSERVED', 'resolved_no_longer_observed'],
  ['RESOLVED_NO_RISK', 'resolved_no_risk'],
  ['RESOLVED_RISK_ACCEPTED', 'resolved_risk_accepted'],
  ['RESOLVED_CONTESTED_ASSET', 'resolved_contested_asset'],
  ['RESOLVED_REMEDIATED_AUTOMATICALLY', 'resolved_remediated_automatically'],
  ['UNDER_INVESTIGATION', 'under_investigation'],
  ['REOPENED', 'reopened'],
  ['RESOLVED', 'resolved'],
  ['NEW', 'new'],
];

export const resolveXpanseAlertStatus = ({
  alert,
}: {
  alert: Pick<XpanseAlert, 'status' | 'resolution_status'>;
}): XpanseAlertStatus | null => {
  if (alert.status) {
    return alert.status;
  }
  const rawStatus = alert.resolution_status?.toUpperCase();
  if (!rawStatus) {
    return null;
  }
  const match = RESOLUTION_STATUS_TOKEN_TO_ALERT_STATUS.find(([token]) =>
    rawStatus.includes(token),
  );
  return match ? match[1] : null;
};

export const isXpanseAlertResolved = ({
  alert,
}: {
  alert: Pick<XpanseAlert, 'status' | 'resolution_status'>;
}): boolean =>
  resolveXpanseAlertStatus({ alert })?.startsWith('resolved') === true;

export const XpanseConnectBodySchema = z.object({
  fqdn: z.string().min(1),
  apiKeyId: z.string().min(1),
  apiKey: z.string().min(1),
  // Xpanse Public API only accepts STANDARD; ADVANCED kept for XSIAM/XDR.
  authType: XpanseAuthTypeSchema.default('STANDARD'),
  severityFilter: z.array(XpanseAlertSeverityFilterSchema).optional(),
  pollIntervalMinutes: z.int().min(1).max(60).optional(),
  firstFetchLookbackDays: z.int().min(1).max(365).optional(),
});
export type XpanseConnectBody = z.infer<typeof XpanseConnectBodySchema>;

export const XpanseSyncStateSchema = z.enum([
  'CONNECTING',
  'BACKFILLING',
  'LISTENING',
  'ERROR',
]);
export type XpanseSyncState = z.infer<typeof XpanseSyncStateSchema>;

export const XpanseAssetImportStateSchema = z.enum([
  'IDLE',
  'IMPORTING',
  'ERROR',
]);
export type XpanseAssetImportState = z.infer<
  typeof XpanseAssetImportStateSchema
>;

export const DEFAULT_XPANSE_SEVERITY_FILTER = [
  'medium',
  'high',
  'critical',
] as const;
export const DEFAULT_XPANSE_POLL_INTERVAL_MINUTES = 5;
export const DEFAULT_XPANSE_FIRST_FETCH_LOOKBACK_DAYS = 30;
export const DEFAULT_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES = 24 * 60;
export const MIN_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES = 60;
export const MAX_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES = 7 * 24 * 60;
export const MAX_XPANSE_ASSET_IMPORT_SURFACES = 20;

// Xpanse asset endpoints are offset paginated with no server-side cursor, and a
// single run only walks a bounded number of pages. Persisting the offset per
// entity lets consecutive runs advance through tenants whose inventory is larger
// than one run can cover, and each offset resets once its full pass completes.
const assetImportOffset = z.number().int().min(0).catch(0).default(0);

export const XpanseAssetImportCursorsSchema = z.object({
  ipRanges: assetImportOffset,
  internetExposureAssets: assetImportOffset,
  externalServices: assetImportOffset,
});
export type XpanseAssetImportCursors = z.infer<
  typeof XpanseAssetImportCursorsSchema
>;

export const createEmptyXpanseAssetImportCursors =
  (): XpanseAssetImportCursors => ({
    ipRanges: 0,
    internetExposureAssets: 0,
    externalServices: 0,
  });

// Every field carries a default so stored provider settings from earlier
// versions parse without a migration.
export const XpanseProviderSettingsSchema = z.object({
  syncState: XpanseSyncStateSchema.catch('CONNECTING').default('CONNECTING'),
  pollIntervalMinutes: z
    .number()
    .catch(DEFAULT_XPANSE_POLL_INTERVAL_MINUTES)
    .default(DEFAULT_XPANSE_POLL_INTERVAL_MINUTES),
  firstFetchLookbackDays: z
    .number()
    .catch(DEFAULT_XPANSE_FIRST_FETCH_LOOKBACK_DAYS)
    .default(DEFAULT_XPANSE_FIRST_FETCH_LOOKBACK_DAYS),
  severityFilter: z
    .array(z.string())
    .catch([...DEFAULT_XPANSE_SEVERITY_FILTER])
    .default([...DEFAULT_XPANSE_SEVERITY_FILTER]),
  lastAlertCreationTimeMs: z.number().nullable().catch(null).default(null),
  lastSyncAt: z.string().nullable().catch(null).default(null),
  lastError: z.string().nullable().catch(null).default(null),
  assetImportSurfaceIds: z.array(z.string()).catch([]).default([]),
  assetImportIntervalMinutes: z
    .number()
    .catch(DEFAULT_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES)
    .default(DEFAULT_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES),
  assetImportState: XpanseAssetImportStateSchema.catch('IDLE').default('IDLE'),
  assetImportCursors: XpanseAssetImportCursorsSchema.catch(
    createEmptyXpanseAssetImportCursors,
  ).default(createEmptyXpanseAssetImportCursors),
  lastAssetImportAt: z.string().nullable().catch(null).default(null),
  lastAssetImportError: z.string().nullable().catch(null).default(null),
});
export type XpanseProviderSettings = z.infer<
  typeof XpanseProviderSettingsSchema
>;

export const XpanseUpdateSettingsBodySchema = z.object({
  severityFilter: z.array(XpanseAlertSeverityFilterSchema).optional(),
  pollIntervalMinutes: z.int().min(1).max(60).optional(),
  firstFetchLookbackDays: z.int().min(1).max(365).optional(),
  assetImportSurfaceIds: z
    .array(z.string().min(1))
    .max(MAX_XPANSE_ASSET_IMPORT_SURFACES)
    .optional(),
  assetImportIntervalMinutes: z
    .int()
    .min(MIN_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES)
    .max(MAX_XPANSE_ASSET_IMPORT_INTERVAL_MINUTES)
    .optional(),
});
export type XpanseUpdateSettingsBody = z.infer<
  typeof XpanseUpdateSettingsBodySchema
>;

export const XpanseConnectionStatusResponseSchema = z.object({
  provider: z.literal('xpanse'),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  providerUserId: z.string().nullable(),
  fqdn: z.string().nullable(),
  authType: XpanseAuthTypeSchema.nullable(),
  syncState: XpanseSyncStateSchema.nullable(),
  pollIntervalMinutes: z.int().nullable(),
  firstFetchLookbackDays: z.int().nullable(),
  severityFilter: z.array(z.string()),
  lastAlertCreationTimeMs: z.int().nullable(),
  lastSyncAt: z.string().nullable(),
  lastError: z.string().nullable(),
  assetImportSurfaceIds: z.array(z.string()),
  assetImportIntervalMinutes: z.int().nullable(),
  assetImportState: XpanseAssetImportStateSchema.nullable(),
  lastAssetImportAt: z.string().nullable(),
  lastAssetImportError: z.string().nullable(),
});
export type XpanseConnectionStatusResponse = z.infer<
  typeof XpanseConnectionStatusResponseSchema
>;
